import {
  type MutationObserverOptions,
  type QueryObserverOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import type { DTO } from "@equidefi/shared/types";
import UserClient from "../clients/UserClient";
import { useApi } from "./useApi";

export const useCreateSavedProfileFromVault = (
  vaultId: string,
  options: MutationObserverOptions<DTO.SavedProfile, unknown, void> = {},
) => {
  const userApi = useApi(UserClient);

  return useMutation<DTO.SavedProfile, unknown, void>({
    mutationFn: () => userApi.createSavedProfileFromVault(vaultId),
    ...options,
  });
};

export const useUpdateSavedProfileFromVault = (
  vaultId: string,
  options: MutationObserverOptions<DTO.SavedProfile, unknown, void> = {},
) => {
  const userApi = useApi(UserClient);

  return useMutation<DTO.SavedProfile, unknown, void>({
    mutationFn: () => userApi.updateSavedProfileFromVault(vaultId),
    ...options,
  });
};

export const useCheckSavedProfileExists = (
  accountType: string,
  options: QueryObserverOptions<{ exists: boolean }> = {},
) => {
  const userApi = useApi(UserClient);

  return useQuery<{ exists: boolean }>({
    queryKey: ["user", "profiles", accountType, "exists"],
    queryFn: () => userApi.checkSavedProfileExists(accountType),
    ...options,
  });
};

export const useSavedProfiles = (options: QueryObserverOptions<DTO.SavedProfile[]> = {}) => {
  const userApi = useApi(UserClient);

  return useQuery<DTO.SavedProfile[]>({
    queryKey: ["user", "profiles"],
    queryFn: () => userApi.getProfiles(),
    ...options,
  });
};

export const useSavedProfile = (profileId: string, options: QueryObserverOptions<DTO.SavedProfile> = {}) => {
  const userApi = useApi(UserClient);

  return useQuery<DTO.SavedProfile>({
    queryKey: ["user", "saved-profiles", profileId],
    queryFn: () => userApi.getSavedProfile(profileId),
    enabled: !!profileId,
    ...options,
  });
};

export const useUpdateSavedProfile = (
  profileId: string,
  options: MutationObserverOptions<DTO.SavedProfile, unknown, Partial<DTO.SavedProfile>> = {},
) => {
  const userApi = useApi(UserClient);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Partial<DTO.SavedProfile>) => userApi.updateSavedProfile(profileId, payload),
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries(["user", "profiles"]);
      queryClient.invalidateQueries(["user", "saved-profiles", profileId]);
      options.onSuccess?.(...args);
    },
  });
};

export const useDeleteSavedProfile = (options: MutationObserverOptions<void, unknown, string> = {}) => {
  const userApi = useApi(UserClient);
  const queryClient = useQueryClient();

  return useMutation((profileId: string) => userApi.deleteSavedProfile(profileId), {
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries(["user", "profiles"]);
      options.onSuccess?.(...args);
    },
  });
};

export const useSavedProfileSigners = (
  profileId: string,
  options: QueryObserverOptions<DTO.SavedProfileSigner[]> = {},
) => {
  const userApi = useApi(UserClient);

  return useQuery<DTO.SavedProfileSigner[]>({
    queryKey: ["user", "saved-profiles", profileId, "signers"],
    queryFn: () => userApi.getSavedProfileSigners(profileId),
    enabled: !!profileId,
    ...options,
  });
};

export const useSavedProfileSigner = (
  profileId: string,
  signerId: string,
  options: QueryObserverOptions<DTO.SavedProfileSigner> = {},
) => {
  const userApi = useApi(UserClient);

  return useQuery<DTO.SavedProfileSigner>({
    queryKey: ["user", "saved-profiles", profileId, "signers", signerId],
    queryFn: () => userApi.getSavedProfileSigner(profileId, signerId),
    enabled: !!profileId && !!signerId,
    ...options,
  });
};

export const useUpdateSavedProfileSigner = (
  profileId: string,
  signerId: string,
  options: MutationObserverOptions<DTO.SavedProfileSigner, unknown, Partial<DTO.SavedProfileSigner>> = {},
) => {
  const userApi = useApi(UserClient);

  return useMutation({
    mutationFn: (payload: Partial<DTO.SavedProfileSigner>) =>
      userApi.updateSavedProfileSigner(profileId, signerId, payload),
    ...options,
  });
};

export const useDeleteSavedProfileSigner = (
  profileId: string,
  options: MutationObserverOptions<void, unknown, string> = {},
) => {
  const userApi = useApi(UserClient);

  return useMutation({
    mutationFn: (signerId: string) => userApi.deleteSavedProfileSigner(profileId, signerId),
    ...options,
  });
};

export const useSavedProfileFiles = (
  profileId: string,
  options: QueryObserverOptions<DTO.AccreditationFile[]> = {},
) => {
  const userApi = useApi(UserClient);

  return useQuery<DTO.AccreditationFile[]>({
    queryKey: ["user", "saved-profiles", profileId, "files"],
    queryFn: () => userApi.getSavedProfileFiles(profileId),
    enabled: !!profileId,
    ...options,
  });
};

export const useSavedProfilePresignedUrl = (profileId: string, options = {}) => {
  const userApi = useApi(UserClient);

  return useMutation(
    async (data: { filename: string; fileExtension: string }) =>
      userApi.getSavedProfilePresignedUrl(profileId, data.filename, data.fileExtension),
    options,
  );
};

export const useStoreSavedProfileFile = (
  profileId: string,
  options: MutationObserverOptions<DTO.AccreditationFile, unknown, Partial<DTO.AccreditationFile>> = {},
) => {
  const userApi = useApi(UserClient);

  return useMutation({
    mutationFn: (payload: Partial<DTO.AccreditationFile>) => userApi.storeSavedProfileFile(profileId, payload),
    ...options,
  });
};

export const useUpdateSavedProfileFile = (
  profileId: string,
  fileId: string,
  options: MutationObserverOptions<DTO.AccreditationFile, unknown, Partial<DTO.AccreditationFile>> = {},
) => {
  const userApi = useApi(UserClient);

  return useMutation({
    mutationFn: (payload: Partial<DTO.AccreditationFile>) => userApi.updateSavedProfileFile(profileId, fileId, payload),
    ...options,
  });
};

export const useDeleteSavedProfileFile = (
  profileId: string,
  options: MutationObserverOptions<void, unknown, string> = {},
) => {
  const userApi = useApi(UserClient);

  return useMutation({
    mutationFn: (fileId: string) => userApi.deleteSavedProfileFile(profileId, fileId),
    ...options,
  });
};
